'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { usePathname, useSearchParams } from 'next/navigation'
import { ZerodhaConnector } from '../ZerodhaConnector'

/**
 * @uiComponent
 * @name GTTOrderHistory
 * @description The GTTOrderHistory component displays Good Till Triggered (GTT) orders on the Zerodha platform.
 * It shows detailed information about GTT orders including trigger prices, limit prices, status,
 * and expiration dates. Features symbol filtering, custom symbol input, and comprehensive
 * GTT order management for automated trading strategies.
 * @location GTT Orders tab in the trading terminal
 * @prop apiKey The API key for Zerodha connector authentication
 * @prop apiSecret The API secret for Zerodha connector authentication
 * @prop accessToken Required access token for GTT order access
 */

/**
 * @uiWorkflow
 * @name View GTT Orders on Zerodha
 * @description How to monitor your Good Till Triggered orders and their status
 * @platform zerodha
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "GTT Orders" tab [component: GTTOrderHistory] [action: click]
 * @step 3 Review all GTT orders sorted by most recent first [component: GTTOrderHistory] [action: observe]
 * @step 4 Use the symbol dropdown to filter by specific stocks [component: GTTOrderHistory] [action: select]
 * @step 5 Check order status (ACTIVE, TRIGGERED, CANCELLED, EXPIRED) [component: GTTOrderHistory] [action: observe]
 * @step 6 Review trigger prices and limit prices for each order [component: GTTOrderHistory] [action: observe]
 * @step 7 Monitor expiration dates and trigger types [component: GTTOrderHistory] [action: observe]
 * @step 8 Use data to manage your automated trading strategies [component: GTTOrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Filter GTT Orders by Stock Symbol on Zerodha
 * @description How to focus on GTT orders for specific stocks
 * @platform zerodha
 * @step 1 Open the GTT Orders tab [component: GTTOrderHistory] [action: click]
 * @step 2 Use the symbol dropdown to select a specific stock [component: GTTOrderHistory] [action: select]
 * @step 3 View only GTT orders related to the selected stock [component: GTTOrderHistory] [action: observe]
 * @step 4 Use "ALL" option to see all GTT orders again [component: GTTOrderHistory] [action: select]
 * @step 5 Enter custom symbols not in the dropdown list [component: GTTOrderHistory] [action: input]
 * @step 6 Review filtered results for better order management [component: GTTOrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Analyze GTT Order Performance on Zerodha
 * @description How to evaluate your automated trading order effectiveness
 * @platform zerodha
 * @step 1 Access the GTT Orders tab [component: GTTOrderHistory] [action: click]
 * @step 2 Review the trigger types (SINGLE, OCO) for your orders [component: GTTOrderHistory] [action: observe]
 * @step 3 Check which orders have been triggered vs still active [component: GTTOrderHistory] [action: observe]
 * @step 4 Monitor expiration dates to avoid order lapses [component: GTTOrderHistory] [action: observe]
 * @step 5 Compare trigger prices with current market prices [component: GTTOrderHistory] [action: observe]
 * @step 6 Evaluate the effectiveness of your price targets [component: GTTOrderHistory] [action: analyze]
 * @step 7 Adjust future GTT strategies based on performance [component: GTTOrderHistory] [action: plan]
 */

interface ZerodhaGTTOrderItem {
  id: string
  symbol: string
  exchange: string
  triggerType: string
  triggerPrice: number
  limitPrice?: number
  quantity: number
  side: string
  status: string
  createdAt: number
  expiresAt?: number
  product?: string
}

// Common stock symbols for the dropdown
const COMMON_SYMBOLS = [
  'ALL', // Show all GTT orders
  'RELIANCE',
  'TCS',
  'HDFCBANK',
  'INFY',
  'ICICIBANK',
  'HINDUNILVR',
  'SBIN',
  'BAJFINANCE',
  'BHARTIARTL',
  'KOTAKBANK',
  'WIPRO',
  'AXISBANK',
  'TATAMOTORS',
  'ADANIENT',
  'NIFTY 50'
]

export default function GTTOrderHistory({ apiKey, apiSecret, accessToken }: { apiKey: string, apiSecret: string, accessToken?: string }) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [gttOrders, setGTTOrders] = useState<ZerodhaGTTOrderItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState('ALL')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  useEffect(() => {
    fetchGTTOrderHistory(selectedSymbol)
  }, [selectedSymbol])

  const fetchGTTOrderHistory = async (symbol: string) => {
    setIsLoading(true)
    setError(null)

    try {
      if (!apiKey || !apiSecret || !accessToken) {
        throw new Error('API Key, Secret, and Access Token are required')
      }

      // Fetch GTT orders from the API endpoint
      const response = await fetch('/api/connectors/zerodha/gtt/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey,
          apiSecret,
          accessToken,
          symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch GTT orders')
      }

      const ordersData = await response.json()

      // Sort orders by timestamp, most recent first
      const sortedOrders = [...ordersData].sort((a, b) => b.createdAt - a.createdAt)
      setGTTOrders(sortedOrders)
    } catch (err: any) {
      console.error('Error fetching GTT order history:', err)
      setError(
        err.message ||
          'Failed to fetch GTT order history. Please ensure you are connected to Zerodha.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }

  const handleCustomSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading GTT orders...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_SYMBOLS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_SYMBOLS.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
            <option value="custom">Custom Symbol</option>
          </select>

          {(showCustomInput || !COMMON_SYMBOLS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_SYMBOLS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. RELIANCE)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          Current: <strong>{selectedSymbol}</strong>
        </div>
      </div>

      {gttOrders.length === 0 ? (
        <div className={styles.noOrders}>
          {selectedSymbol === 'ALL' 
            ? 'No GTT orders found' 
            : `No GTT orders found for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Trigger Type</th>
                <th>Trigger Price</th>
                <th>Limit Price</th>
                <th>Quantity</th>
                <th>Status</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {gttOrders.map((order, index) => (
                <tr key={index}>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{order.symbol}</td>
                  <td
                    className={
                      order.side === 'BUY' ? styles.buyOrder : styles.sellOrder
                    }
                  >
                    {order.side}
                  </td>
                  <td>{order.triggerType}</td>
                  <td>₹{order.triggerPrice}</td>
                  <td>{order.limitPrice ? `₹${order.limitPrice}` : '-'}</td>
                  <td>{order.quantity}</td>
                  <td>{order.status}</td>
                  <td>{order.expiresAt ? formatDate(order.expiresAt) : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}